import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { getPostHogRuntime, initPostHog, type PostHogRuntime } from "./client";

interface PostHogContextValue extends PostHogRuntime {
  /** Monotonically increments each time the set of evaluated flags changes. Used to trigger re-renders. */
  readonly flagsRevision: number;
}

const PostHogContext = createContext<PostHogContextValue | null>(null);

interface PostHogProviderProps {
  children: ReactNode;
}

/**
 * Wraps the app with PostHog context. Initializes the PostHog singleton on
 * mount when env vars are present; otherwise provides a no-op runtime so that
 * `useFeatureFlag(...)` always resolves to the caller-supplied default.
 */
export function PostHogProvider({ children }: PostHogProviderProps) {
  const [flagsRevision, setFlagsRevision] = useState(0);
  const runtime = useMemo(() => initPostHog(), []);

  useEffect(() => {
    if (!runtime.enabled) return;
    // PostHog fires this once flags are first loaded and again whenever they change.
    const unsubscribe = runtime.client.onFeatureFlags(() => {
      setFlagsRevision((r) => r + 1);
    });
    return () => {
      unsubscribe?.();
    };
  }, [runtime]);

  const value = useMemo<PostHogContextValue>(
    () => ({ ...runtime, flagsRevision }),
    [runtime, flagsRevision]
  );

  return <PostHogContext.Provider value={value}>{children}</PostHogContext.Provider>;
}

/**
 * Access the PostHog runtime from inside the provider. Throws if used outside.
 * Most callers should prefer `useFeatureFlag` — this is for capture/identify.
 */
export function usePostHogRuntime(): PostHogContextValue {
  const ctx = useContext(PostHogContext);
  if (!ctx) {
    // Fallback for components rendered outside the provider (e.g. error boundaries
    // mounted above App). Returns the singleton without subscribing to updates.
    return { ...getPostHogRuntime(), flagsRevision: 0 };
  }
  return ctx;
}
